/* eslint-disable no-console */
import { Vector3 } from 'three';
import { IBuildingProviderContext } from '../@types/ContextProviderRelated';
import { EWall } from '../@types/Enums';
import BoundaryEdge from '../models/abstract/BoundaryEdge';
import Wall from '../models/abstract/Wall';
import PartitionWallModel from '../models/standard/PartitionWallModel';
import PhysicalBoundaryModel from '../models/standard/PhysicalBoundary';
import RoomModel from '../models/standard/RoomModel';
import SpaceModel from '../models/standard/SpaceModel';
import StandardWallModel from '../models/standard/StandardWallModel';
import VirtualBoundaryModel from '../models/standard/VirtualBoundaryModel';

const EPS = 1e-4;
const TOPO_EPS = 0.05;
const FACE_EPS = 0.15;

type Side = 'A' | 'B';

type TopologyIssue =
  | { type: 'missing_wall_ref'; levelUuid?: string; roomUuid?: string; spaceUuid?: string; wallUuid?: string }
  | { type: 'duplicate_wall_in_array'; scope: string; uuid: string }
  | { type: 'partition_wall_in_physical_boundary'; roomUuid?: string; spaceUuid?: string; wallUuid: string }
  | { type: 'physical_boundary_too_small'; roomUuid?: string; spaceUuid?: string; edgeCount: number }
  | { type: 'edge_wall_not_in_building'; roomUuid?: string; spaceUuid?: string; wallUuid: string }
  | { type: 'edge_zero_length_param'; roomUuid?: string; spaceUuid?: string; wallUuid?: string }
  | { type: 'boundary_walls_mismatch'; roomUuid?: string; spaceUuid?: string }
  | { type: 'wall_used_more_than_two_sides'; wallUuid: string; sides: Side[] }
  | { type: 'space_has_duplicate_walls'; roomUuid?: string; spaceUuid?: string; uuids: string[] }
  | { type: 'boundary_not_continuous'; roomUuid?: string; spaceUuid?: string }
  | { type: 'edge_not_on_wall_face'; roomUuid?: string; spaceUuid?: string; wallUuid?: string; side?: Side }
  | { type: 'final_validation_failed'; message: string };

interface NormalizeTopologyOptions {
  debug?: boolean;
  mutate?: boolean;

  /**
   * Wichtig für V2:
   * Wenn BoundaryEdges bereits explizite setPoints() haben,
   * dürfen diese nicht aus wall+side+t0/t1 überschrieben werden.
   */
  preserveImportedBoundaryGeometry?: boolean;

  /**
   * Wichtig für V2:
   * Side A/B kommt schon aus dem Import und sollte nicht neu geraten werden.
   */
  preserveBoundarySides?: boolean;

  /**
   * Für native neue Pläne kann man streng prüfen.
   * Für migrierte V2-Pläne sollte das false sein.
   */
  strictFaceValidation?: boolean;
}

interface NormalizeTopologyResult {
  building: IBuildingProviderContext;
  issues: TopologyIssue[];
  isValid: boolean;
}

type FaceSegment = {
  start: Vector3;
  end: Vector3;
};

const isStandardWall = (w: Wall | undefined | null): w is StandardWallModel =>
  !!w &&
  !(w instanceof PartitionWallModel) &&
  (w instanceof StandardWallModel || w.type === EWall.STANDARD || w.type === undefined || w.type === null);

const isPartitionWall = (w: Wall | undefined | null): w is PartitionWallModel =>
  !!w && (w instanceof PartitionWallModel || w.type === EWall.PARTITION || (w as any).type === 'partition');

function log(debug: boolean, ...args: any[]) {
  if (debug) console.log('[normalizeBuildingTopology]', ...args);
}

function warn(debug: boolean, ...args: any[]) {
  if (debug) console.warn('[normalizeBuildingTopology]', ...args);
}

function vecEq2(a: Vector3, b: Vector3, eps = TOPO_EPS) {
  return Math.abs(a.x - b.x) <= eps && Math.abs(a.y - b.y) <= eps;
}

function samePt3(a: Vector3, b: Vector3, eps = TOPO_EPS) {
  return Math.abs(a.x - b.x) <= eps && Math.abs(a.y - b.y) <= eps && Math.abs((a.z ?? 0) - (b.z ?? 0)) <= eps;
}

function dedupeWalls<T extends { uuid: string }>(items: T[]): T[] {
  const map = new Map<string, T>();

  items.forEach((item) => {
    if (item?.uuid && !map.has(item.uuid)) {
      map.set(item.uuid, item);
    }
  });

  return Array.from(map.values());
}

function toVector3Maybe(p: any): Vector3 | null {
  if (!p) return null;
  if (p instanceof Vector3) return p.clone();

  if (typeof p.x === 'number' && typeof p.y === 'number') {
    return new Vector3(p.x, p.y, p.z ?? 0);
  }

  return null;
}

function getStoredExplicitEdgeGeometry(edge: BoundaryEdge): { start: Vector3; end: Vector3 } | null {
  const start = toVector3Maybe((edge as any)._startPoint);
  const end = toVector3Maybe((edge as any)._endPoint);

  if (!start || !end) return null;

  return { start, end };
}

function hasExplicitEdgeGeometry(edge: BoundaryEdge): boolean {
  return getStoredExplicitEdgeGeometry(edge) !== null;
}

function cloneBoundaryEdge(edge: BoundaryEdge, patch?: Partial<BoundaryEdge>): BoundaryEdge {
  const nextReversed = patch?.reversed ?? edge.reversed;

  const out = new BoundaryEdge(
    patch?.wall ?? edge.wall,
    patch?.t0 ?? edge.t0,
    patch?.t1 ?? edge.t1,
    (patch?.side as Side | undefined) ?? (edge.side as Side),
    nextReversed,
  );

  if ((edge as any).uuid) {
    out.setUUID((edge as any).uuid);
  }

  const explicit = getStoredExplicitEdgeGeometry(edge);
  if (explicit) {
    const reversedChanged = patch?.reversed !== undefined && patch.reversed !== edge.reversed;

    if (reversedChanged) {
      out.setPoints(explicit.end.clone(), explicit.start.clone());
    } else {
      out.setPoints(explicit.start.clone(), explicit.end.clone());
    }
  }

  return out;
}

function getStdWallFaceSegment(wall: StandardWallModel, side: Side): FaceSegment {
  const w: any = wall;

  const outerStart = w.outerStartPoint
    ? new Vector3(w.outerStartPoint.x, w.outerStartPoint.y, w.outerStartPoint.z ?? 0)
    : null;
  const outerEnd = w.outerEndPoint
    ? new Vector3(w.outerEndPoint.x, w.outerEndPoint.y, w.outerEndPoint.z ?? 0)
    : null;
  const innerStart = w.outerStartPoint2
    ? new Vector3(w.outerStartPoint2.x, w.outerStartPoint2.y, w.outerStartPoint2.z ?? 0)
    : null;
  const innerEnd = w.outerEndPoint2
    ? new Vector3(w.outerEndPoint2.x, w.outerEndPoint2.y, w.outerEndPoint2.z ?? 0)
    : null;

  if (outerStart && outerEnd && innerStart && innerEnd) {
    return side === 'A'
      ? { start: innerStart, end: innerEnd }
      : { start: outerStart, end: outerEnd };
  }

  const dir = wall.endPoint.clone().sub(wall.startPoint);
  const len = dir.length();

  if (len < EPS) {
    return {
      start: wall.startPoint.clone(),
      end: wall.endPoint.clone(),
    };
  }

  dir.divideScalar(len);

  const left = new Vector3(-dir.y, dir.x, 0);
  const right = new Vector3(dir.y, -dir.x, 0);
  const half = (wall.depth ?? 0) * 0.5;

  const offset = side === 'A' ? left.multiplyScalar(half) : right.multiplyScalar(half);

  return {
    start: wall.startPoint.clone().add(offset),
    end: wall.endPoint.clone().add(offset),
  };
}

function getWallFaceSegment(wall: Wall, side: Side): FaceSegment {
  if (isStandardWall(wall)) {
    return getStdWallFaceSegment(wall, side);
  }

  return {
    start: wall.startPoint.clone(),
    end: wall.endPoint.clone(),
  };
}

function getEdgeFaceGeometry(edge: BoundaryEdge): { start: Vector3; end: Vector3 } {
  const face = getWallFaceSegment(edge.wall, (edge.side as Side) ?? 'A');

  const p0 = face.start.clone().lerp(face.end, edge.t0);
  const p1 = face.start.clone().lerp(face.end, edge.t1);

  return edge.reversed
    ? { start: p1, end: p0 }
    : { start: p0, end: p1 };
}

function getEdgeValidationGeometry(edge: BoundaryEdge): { start: Vector3; end: Vector3 } {
  return getStoredExplicitEdgeGeometry(edge) ?? getEdgeFaceGeometry(edge);
}

function getEdgeOrderingGeometry(edge: BoundaryEdge, preferExplicit: boolean): { start: Vector3; end: Vector3 } {
  if (preferExplicit) {
    const explicit = getStoredExplicitEdgeGeometry(edge);
    if (explicit) return explicit;
  }

  return getEdgeFaceGeometry(edge);
}

function setExplicitEdgePointsFromFace(edge: BoundaryEdge, preserveExisting = false) {
  if (preserveExisting && hasExplicitEdgeGeometry(edge)) return;

  const geom = getEdgeFaceGeometry(edge);
  edge.setPoints(geom.start, geom.end);
}

function rebuildExplicitBoundaryPolygonPoints(boundary: PhysicalBoundaryModel, preserveExisting = false) {
  const edges = Array.isArray(boundary.edges) ? (boundary.edges as BoundaryEdge[]) : [];

  edges.forEach((edge) => {
    setExplicitEdgePointsFromFace(edge, preserveExisting);
  });
}

function edgeMatchesWallFace(edge: BoundaryEdge, eps = FACE_EPS): boolean {
  const explicit = getStoredExplicitEdgeGeometry(edge);
  const geom = getEdgeFaceGeometry(edge);

  if (!explicit) {
    const currentStart = edge.startPoint;
    const currentEnd = edge.endPoint;

    return samePt3(currentStart, geom.start, eps) && samePt3(currentEnd, geom.end, eps);
  }

  return samePt3(explicit.start, geom.start, eps) && samePt3(explicit.end, geom.end, eps);
}

function ensureContinuousClockwiseEdges(
  edges: BoundaryEdge[],
  debug: boolean,
  preferExplicitGeometry = false,
): BoundaryEdge[] {
  if (edges.length < 3) return edges;

  const used = new Array(edges.length).fill(false);
  const ordered: BoundaryEdge[] = [];

  ordered.push(edges[0]);
  used[0] = true;

  let cursor = getEdgeOrderingGeometry(edges[0], preferExplicitGeometry).end.clone();

  for (let count = 1; count < edges.length; count++) {
    let found = false;

    for (let i = 1; i < edges.length; i++) {
      if (used[i]) continue;

      const geom = getEdgeOrderingGeometry(edges[i], preferExplicitGeometry);

      if (vecEq2(cursor, geom.start)) {
        ordered.push(edges[i]);
        cursor = geom.end.clone();
        used[i] = true;
        found = true;
        break;
      }

      if (vecEq2(cursor, geom.end)) {
        const flipped = cloneBoundaryEdge(edges[i], { reversed: !edges[i].reversed });

        if (!preferExplicitGeometry || !hasExplicitEdgeGeometry(flipped)) {
          setExplicitEdgePointsFromFace(flipped);
        }

        ordered.push(flipped);
        cursor = getEdgeOrderingGeometry(flipped, preferExplicitGeometry).end.clone();
        used[i] = true;
        found = true;
        break;
      }
    }

    if (!found) {
      warn(debug, 'Could not perfectly chain edges, forcing remaining edge into loop');

      const idx = used.findIndex((x) => !x);
      if (idx === -1) break;

      if (!preferExplicitGeometry || !hasExplicitEdgeGeometry(edges[idx])) {
        setExplicitEdgePointsFromFace(edges[idx]);
      }

      ordered.push(edges[idx]);
      used[idx] = true;
      cursor = getEdgeOrderingGeometry(edges[idx], preferExplicitGeometry).end.clone();
    }
  }

  const pts = ordered.map((e) => getEdgeOrderingGeometry(e, preferExplicitGeometry).start);
  let area = 0;

  for (let i = 0; i < pts.length; i++) {
    const p1 = pts[i];
    const p2 = pts[(i + 1) % pts.length];
    area += (p2.x - p1.x) * (p2.y + p1.y);
  }

  if (area < 0) {
    return ordered
      .map((e) => {
        const flipped = cloneBoundaryEdge(e, { reversed: !e.reversed });

        if (!preferExplicitGeometry || !hasExplicitEdgeGeometry(flipped)) {
          setExplicitEdgePointsFromFace(flipped);
        }

        return flipped;
      })
      .reverse();
  }

  ordered.forEach((e) => {
    if (!preferExplicitGeometry || !hasExplicitEdgeGeometry(e)) {
      setExplicitEdgePointsFromFace(e);
    }
  });

  return ordered;
}

function collectLevelWallsIntoMap(building: IBuildingProviderContext, out: Map<string, Wall>) {
  const levels = (building as any).levels;
  if (!Array.isArray(levels)) return;

  levels.forEach((level: any) => {
    (level.walls ?? []).forEach((w: Wall) => {
      if (w?.uuid) out.set(w.uuid, w);
    });

    (level.rooms ?? []).forEach((room: any) => {
      (room.spaces ?? []).forEach((space: any) => {
        (space.walls ?? []).forEach((w: Wall) => {
          if (w?.uuid) out.set(w.uuid, w);
        });

        (space.boundaries ?? []).forEach((b: any) => {
          if (!(b instanceof PhysicalBoundaryModel)) return;

          (b.walls ?? []).forEach((w: Wall) => {
            if (w?.uuid) out.set(w.uuid, w);
          });

          (b.edges ?? []).forEach((e: BoundaryEdge) => {
            if (e?.wall?.uuid) out.set(e.wall.uuid, e.wall);
          });
        });
      });
    });
  });
}

function collectAllWalls(building: IBuildingProviderContext): Map<string, Wall> {
  const out = new Map<string, Wall>();

  (building.walls ?? []).forEach((w) => {
    if (w?.uuid) out.set(w.uuid, w);
  });

  collectLevelWallsIntoMap(building, out);

  (building.rooms ?? []).forEach((room: any) => {
    (room.spaces ?? []).forEach((space: any) => {
      (space.walls ?? []).forEach((w: Wall) => {
        if (w?.uuid) out.set(w.uuid, w);
      });

      (space.boundaries ?? []).forEach((b: any) => {
        if (!(b instanceof PhysicalBoundaryModel)) return;

        (b.walls ?? []).forEach((w: Wall) => {
          if (w?.uuid) out.set(w.uuid, w);
        });

        (b.edges ?? []).forEach((e: BoundaryEdge) => {
          if (e?.wall?.uuid) out.set(e.wall.uuid, e.wall);
        });
      });
    });
  });

  return out;
}

function relinkAllWallRefs(
  building: IBuildingProviderContext,
  issues: TopologyIssue[],
  debug: boolean,
  preserveImportedBoundaryGeometry = false,
) {
  const canonicalWalls = collectAllWalls(building);

  (building.rooms ?? []).forEach((room: any) => {
    (room.spaces ?? []).forEach((space: any) => {
      if (Array.isArray(space.walls)) {
        space.walls = dedupeWalls(
          space.walls
            .map((w: Wall) => {
              const resolved = w?.uuid ? canonicalWalls.get(w.uuid) : undefined;

              if (!resolved && w?.uuid) {
                issues.push({
                  type: 'missing_wall_ref',
                  roomUuid: room.uuid,
                  spaceUuid: space.uuid,
                  wallUuid: w.uuid,
                });
              }

              return resolved;
            })
            .filter(Boolean) as Wall[],
        );
      }

      if (Array.isArray(space.boundaries)) {
        space.boundaries = space.boundaries.map((boundary: any) => {
          if (!(boundary instanceof PhysicalBoundaryModel)) return boundary;

          if (Array.isArray(boundary.edges)) {
            boundary.edges = boundary.edges
              .map((edge: BoundaryEdge) => {
                const resolved = edge.wall?.uuid ? canonicalWalls.get(edge.wall.uuid) : undefined;

                if (!resolved) {
                  issues.push({
                    type: 'missing_wall_ref',
                    roomUuid: room.uuid,
                    spaceUuid: space.uuid,
                    wallUuid: edge.wall?.uuid,
                  });

                  return null;
                }

                const cloned = cloneBoundaryEdge(edge, { wall: resolved });

                if (!preserveImportedBoundaryGeometry || !hasExplicitEdgeGeometry(cloned)) {
                  setExplicitEdgePointsFromFace(cloned);
                }

                return cloned;
              })
              .filter(Boolean) as BoundaryEdge[];
          }

          if (Array.isArray(boundary.walls)) {
            boundary.walls = dedupeWalls(
              boundary.walls
                .map((w: Wall) => (w?.uuid ? canonicalWalls.get(w.uuid) : undefined))
                .filter(Boolean) as Wall[],
            );
          }

          return boundary;
        });
      }
    });
  });

  building.walls = Array.from(canonicalWalls.values());

  log(debug, 'Relinked wall references');
}

function cleanPhysicalBoundariesFromImportedData(
  building: IBuildingProviderContext,
  debug: boolean,
  preserveImportedBoundaryGeometry = false,
) {
  (building.rooms ?? []).forEach((room: any) => {
    (room.spaces ?? []).forEach((space: any) => {
      (space.boundaries ?? []).forEach((boundary: any) => {
        if (!(boundary instanceof PhysicalBoundaryModel)) return;

        let edges = Array.isArray(boundary.edges) ? (boundary.edges as BoundaryEdge[]) : [];

        edges = edges.filter((e) => !!e?.wall && !isPartitionWall(e.wall));

        edges.forEach((e) => {
          e.t0 = Math.max(0, Math.min(1, Number.isFinite(e.t0) ? e.t0 : 0));
          e.t1 = Math.max(0, Math.min(1, Number.isFinite(e.t1) ? e.t1 : 1));

          if (!e.side) e.side = 'A';

          setExplicitEdgePointsFromFace(e, preserveImportedBoundaryGeometry);
        });

        edges = ensureContinuousClockwiseEdges(edges, debug, preserveImportedBoundaryGeometry);

        boundary.setEdges(edges);
      });
    });
  });

  log(debug, 'Cleaned physical boundaries from imported boundary data');
}

function syncBoundaryWallsFromEdges(building: IBuildingProviderContext, debug: boolean) {
  (building.rooms ?? []).forEach((room: any) => {
    (room.spaces ?? []).forEach((space: any) => {
      (space.boundaries ?? []).forEach((boundary: any) => {
        if (!(boundary instanceof PhysicalBoundaryModel)) return;

        const edges = Array.isArray(boundary.edges) ? (boundary.edges as BoundaryEdge[]) : [];
        boundary.walls = dedupeWalls(edges.map((e) => e.wall).filter(Boolean));
      });
    });
  });

  log(debug, 'Synced boundary.walls from boundary.edges');
}

function assignBoundarySides(
  building: IBuildingProviderContext,
  debug: boolean,
  preserveBoundarySides = false,
  preserveImportedBoundaryGeometry = false,
) {
  const wallUsage = new Map<string, Set<Side>>();
  const spaceWallAssignedSide = new Map<string, Side>();

  const rememberSide = (wallUuid: string, side: Side) => {
    if (!wallUsage.has(wallUuid)) {
      wallUsage.set(wallUuid, new Set<Side>());
    }

    wallUsage.get(wallUuid)!.add(side);
  };

  (building.rooms ?? []).forEach((room: any) => {
    (room.spaces ?? []).forEach((space: any) => {
      (space.boundaries ?? []).forEach((boundary: any) => {
        if (!(boundary instanceof PhysicalBoundaryModel)) return;

        const edges = Array.isArray(boundary.edges) ? (boundary.edges as BoundaryEdge[]) : [];

        edges.forEach((edge) => {
          const wallUuid = edge.wall?.uuid;
          if (!wallUuid) return;

          const key = `${space.uuid}_${wallUuid}`;

          if (preserveBoundarySides && (edge.side === 'A' || edge.side === 'B')) {
            const side = edge.side as Side;

            rememberSide(wallUuid, side);
            spaceWallAssignedSide.set(key, side);
            setExplicitEdgePointsFromFace(edge, preserveImportedBoundaryGeometry);

            return;
          }

          if (spaceWallAssignedSide.has(key)) {
            edge.side = spaceWallAssignedSide.get(key)!;
            rememberSide(wallUuid, edge.side as Side);
            setExplicitEdgePointsFromFace(edge, preserveImportedBoundaryGeometry);

            return;
          }

          let side: Side = 'A';

          if (!wallUsage.has(wallUuid)) {
            wallUsage.set(wallUuid, new Set<Side>(['A']));
            side = 'A';
          } else {
            const used = wallUsage.get(wallUuid)!;

            if (used.has('A') && !used.has('B')) {
              side = 'B';
              used.add('B');
            } else if (!used.has('A')) {
              side = 'A';
              used.add('A');
            } else {
              side = 'B';
            }
          }

          edge.side = side;
          spaceWallAssignedSide.set(key, side);
          setExplicitEdgePointsFromFace(edge, preserveImportedBoundaryGeometry);
        });
      });
    });
  });

  log(debug, 'Assigned boundary sides');
}

function syncSpaceWallsFromBoundaries(building: IBuildingProviderContext, debug: boolean) {
  (building.rooms ?? []).forEach((room: any) => {
    (room.spaces ?? []).forEach((space: any) => {
      const physicals = (space.boundaries ?? []).filter(
        (b: any) => b instanceof PhysicalBoundaryModel,
      ) as PhysicalBoundaryModel[];

      const partitionWalls = (space.walls ?? []).filter(isPartitionWall);

      const boundaryWalls = dedupeWalls(
        physicals
          .flatMap((b) => (b.edges ?? []).map((e: BoundaryEdge) => e.wall))
          .filter(Boolean),
      );

      space.walls = dedupeWalls([...boundaryWalls, ...partitionWalls]);
    });
  });

  log(debug, 'Synced space.walls from boundaries');
}

function recollectBuildingWalls(building: IBuildingProviderContext, debug: boolean) {
  const map = new Map<string, Wall>();

  // Wichtig:
  // Bestehende globale/level-only Wände behalten.
  // Sonst verschwinden PartitionWalls, die nicht in einem Space hängen.
  (building.walls ?? []).forEach((w) => {
    if (w?.uuid) map.set(w.uuid, w);
  });

  collectLevelWallsIntoMap(building, map);

  (building.rooms ?? []).forEach((room: any) => {
    (room.spaces ?? []).forEach((space: any) => {
      (space.walls ?? []).forEach((w: Wall) => {
        if (w?.uuid) map.set(w.uuid, w);
      });

      (space.boundaries ?? []).forEach((boundary: any) => {
        if (!(boundary instanceof PhysicalBoundaryModel)) return;

        (boundary.walls ?? []).forEach((w: Wall) => {
          if (w?.uuid) map.set(w.uuid, w);
        });

        (boundary.edges ?? []).forEach((e: BoundaryEdge) => {
          if (e?.wall?.uuid) map.set(e.wall.uuid, e.wall);
        });
      });
    });
  });

  building.walls = Array.from(map.values());

  log(debug, 'Recollected building.walls', building.walls.length);
}

function rebuildExplicitBoundaryPoints(
  building: IBuildingProviderContext,
  debug: boolean,
  preserveExisting = false,
) {
  (building.rooms ?? []).forEach((room: any) => {
    (room.spaces ?? []).forEach((space: any) => {
      (space.boundaries ?? []).forEach((boundary: any) => {
        if (!(boundary instanceof PhysicalBoundaryModel)) return;

        rebuildExplicitBoundaryPolygonPoints(boundary, preserveExisting);
      });
    });
  });

  log(debug, 'Rebuilt explicit boundary edge polygon points');
}

function validateTopology(
  building: IBuildingProviderContext,
  debug: boolean,
  strictFaceValidation = true,
): TopologyIssue[] {
  const issues: TopologyIssue[] = [];
  const buildingWallIds = new Set((building.walls ?? []).map((w) => w.uuid));
  const globalSideUsage = new Map<string, Set<Side>>();

  const checkDupes = (scope: string, items: Array<{ uuid: string }>) => {
    const seen = new Set<string>();

    items.forEach((item) => {
      if (!item?.uuid) return;

      if (seen.has(item.uuid)) {
        issues.push({
          type: 'duplicate_wall_in_array',
          scope,
          uuid: item.uuid,
        });
      }

      seen.add(item.uuid);
    });
  };

  checkDupes('building.walls', building.walls ?? []);

  (building.rooms ?? []).forEach((room: any) => {
    (room.spaces ?? []).forEach((space: any) => {
      const spaceWalls = (space.walls ?? []) as Wall[];

      checkDupes(`space.walls:${space.uuid}`, spaceWalls);

      const dupUuids = spaceWalls
        .map((w) => w.uuid)
        .filter((u, i, arr) => arr.indexOf(u) !== i);

      if (dupUuids.length > 0) {
        issues.push({
          type: 'space_has_duplicate_walls',
          roomUuid: room.uuid,
          spaceUuid: space.uuid,
          uuids: Array.from(new Set(dupUuids)),
        });
      }

      (space.boundaries ?? []).forEach((boundary: any) => {
        if (!(boundary instanceof PhysicalBoundaryModel)) return;

        const edges = (boundary.edges ?? []) as BoundaryEdge[];
        const bwalls = (boundary.walls ?? []) as Wall[];

        checkDupes(`boundary.walls:${space.uuid}`, bwalls);

        if (edges.length < 3) {
          issues.push({
            type: 'physical_boundary_too_small',
            roomUuid: room.uuid,
            spaceUuid: space.uuid,
            edgeCount: edges.length,
          });
        }

        for (const edge of edges) {
          if (!edge.wall?.uuid || !buildingWallIds.has(edge.wall.uuid)) {
            issues.push({
              type: 'edge_wall_not_in_building',
              roomUuid: room.uuid,
              spaceUuid: space.uuid,
              wallUuid: edge.wall?.uuid,
            });
          }

          if (isPartitionWall(edge.wall)) {
            issues.push({
              type: 'partition_wall_in_physical_boundary',
              roomUuid: room.uuid,
              spaceUuid: space.uuid,
              wallUuid: edge.wall.uuid,
            });
          }

          const validationGeom = getEdgeValidationGeometry(edge);
          const validationLength = validationGeom.start.distanceTo(validationGeom.end);

          if (Math.abs(edge.t1 - edge.t0) < 1e-6 && validationLength < EPS) {
            issues.push({
              type: 'edge_zero_length_param',
              roomUuid: room.uuid,
              spaceUuid: space.uuid,
              wallUuid: edge.wall?.uuid,
            });
          }

          if (strictFaceValidation && !edgeMatchesWallFace(edge)) {
            issues.push({
              type: 'edge_not_on_wall_face',
              roomUuid: room.uuid,
              spaceUuid: space.uuid,
              wallUuid: edge.wall?.uuid,
              side: edge.side as Side,
            });
          }

          if (edge.wall?.uuid && edge.side) {
            if (!globalSideUsage.has(edge.wall.uuid)) {
              globalSideUsage.set(edge.wall.uuid, new Set<Side>());
            }

            globalSideUsage.get(edge.wall.uuid)!.add(edge.side as Side);
          }
        }

        const uniqEdgeWalls = dedupeWalls(edges.map((e) => e.wall));
        const uniqBoundaryWalls = dedupeWalls(bwalls);

        const a = uniqEdgeWalls.map((w) => w.uuid).sort().join('|');
        const b = uniqBoundaryWalls.map((w) => w.uuid).sort().join('|');

        if (a !== b) {
          issues.push({
            type: 'boundary_walls_mismatch',
            roomUuid: room.uuid,
            spaceUuid: space.uuid,
          });
        }

        let continuous = true;

        for (let i = 0; i < edges.length; i++) {
          const curGeom = getEdgeValidationGeometry(edges[i]);
          const nxtGeom = getEdgeValidationGeometry(edges[(i + 1) % edges.length]);

          if (!vecEq2(curGeom.end, nxtGeom.start)) {
            continuous = false;
            break;
          }
        }

        if (!continuous) {
          issues.push({
            type: 'boundary_not_continuous',
            roomUuid: room.uuid,
            spaceUuid: space.uuid,
          });
        }
      });
    });
  });

  globalSideUsage.forEach((sides, wallUuid) => {
    if (sides.size > 2) {
      issues.push({
        type: 'wall_used_more_than_two_sides',
        wallUuid,
        sides: Array.from(sides),
      });
    }
  });

  if (debug) {
    if (issues.length === 0) {
      log(debug, 'Topology validation passed with 0 issues');
    } else {
      warn(debug, 'Topology validation found issues:', issues);
    }
  }

  return issues;
}

function finalValidation(building: IBuildingProviderContext, debug: boolean): TopologyIssue[] {
  const issues: TopologyIssue[] = [];

  (building.rooms ?? []).forEach((room: any) => {
    (room.spaces ?? []).forEach((space: any) => {
      const physical = (space.boundaries ?? []).find(
        (b: any) => b instanceof PhysicalBoundaryModel,
      ) as PhysicalBoundaryModel | undefined;

      if (!physical) return;

      const edges = (physical.edges ?? []) as BoundaryEdge[];

      if (edges.length < 3) {
        issues.push({
          type: 'final_validation_failed',
          message: `Space ${space.uuid} has less than 3 boundary edges`,
        });

        return;
      }

      for (let i = 0; i < edges.length; i++) {
        const cur = edges[i];
        const nxt = edges[(i + 1) % edges.length];

        const curGeom = getEdgeValidationGeometry(cur);
        const nxtGeom = getEdgeValidationGeometry(nxt);

        if (!vecEq2(curGeom.end, nxtGeom.start)) {
          issues.push({
            type: 'final_validation_failed',
            message: `Space ${space.uuid} boundary loop is not continuous`,
          });

          break;
        }
      }
    });
  });

  if (issues.length === 0) {
    log(debug, 'Final validation passed');
  } else {
    warn(debug, 'Final validation failed', issues);
  }

  return issues;
}

function cloneBuildingShallow(building: IBuildingProviderContext): IBuildingProviderContext {
  const next: any = { ...building };

  next.walls = [...(building.walls ?? [])];

  if (Array.isArray((building as any).levels)) {
    next.levels = (building as any).levels.map((level: any) => {
      const nextLevel = Object.assign(Object.create(Object.getPrototypeOf(level)), level);

      nextLevel.walls = [...(level.walls ?? [])];

      nextLevel.rooms = (level.rooms ?? []).map((room: any) => {
        const nextRoom = Object.assign(Object.create(Object.getPrototypeOf(room) ?? RoomModel.prototype), room);

        nextRoom.spaces = (room.spaces ?? []).map((space: any) => {
          const nextSpace = Object.assign(Object.create(Object.getPrototypeOf(space) ?? SpaceModel.prototype), space);

          nextSpace.walls = [...(space.walls ?? [])];
          nextSpace.boundaries = cloneBoundariesArray(space.boundaries ?? []);

          return nextSpace;
        });

        return nextRoom;
      });

      return nextLevel;
    });
  }

  next.rooms = (building.rooms ?? []).map((room: any) => {
    const nextRoom = Object.assign(Object.create(Object.getPrototypeOf(room) ?? RoomModel.prototype), room);

    nextRoom.spaces = (room.spaces ?? []).map((space: any) => {
      const nextSpace = Object.assign(Object.create(Object.getPrototypeOf(space) ?? SpaceModel.prototype), space);

      nextSpace.walls = [...(space.walls ?? [])];
      nextSpace.boundaries = cloneBoundariesArray(space.boundaries ?? []);

      return nextSpace;
    });

    return nextRoom;
  });

  return next;
}

function cloneBoundariesArray(boundaries: any[]) {
  return boundaries.map((boundary: any) => {
    if (boundary instanceof PhysicalBoundaryModel) {
      const nextBoundary = Object.assign(
        Object.create(Object.getPrototypeOf(boundary) ?? PhysicalBoundaryModel.prototype),
        boundary,
      );

      nextBoundary.walls = [...(boundary.walls ?? [])];
      nextBoundary.edges = (boundary.edges ?? []).map((edge: BoundaryEdge) => cloneBoundaryEdge(edge));

      return nextBoundary;
    }

    if (boundary instanceof VirtualBoundaryModel) {
      return Object.assign(
        Object.create(Object.getPrototypeOf(boundary) ?? VirtualBoundaryModel.prototype),
        boundary,
      );
    }

    return boundary;
  });
}

export function normalizeBuildingTopology(
  inputBuilding: IBuildingProviderContext,
  options: NormalizeTopologyOptions = {},
): NormalizeTopologyResult {
  const debug = !!options.debug;
  const building = options.mutate ? inputBuilding : cloneBuildingShallow(inputBuilding);
  const issues: TopologyIssue[] = [];

  const preserveImportedBoundaryGeometry = !!options.preserveImportedBoundaryGeometry;
  const preserveBoundarySides = options.preserveBoundarySides ?? preserveImportedBoundaryGeometry;
  const strictFaceValidation = options.strictFaceValidation ?? !preserveImportedBoundaryGeometry;

  log(debug, 'Starting normalization');

  relinkAllWallRefs(building, issues, debug, preserveImportedBoundaryGeometry);
  cleanPhysicalBoundariesFromImportedData(building, debug, preserveImportedBoundaryGeometry);
  syncBoundaryWallsFromEdges(building, debug);
  assignBoundarySides(building, debug, preserveBoundarySides, preserveImportedBoundaryGeometry);
  syncSpaceWallsFromBoundaries(building, debug);
  recollectBuildingWalls(building, debug);
  rebuildExplicitBoundaryPoints(building, debug, preserveImportedBoundaryGeometry);

  const validationIssues = validateTopology(building, debug, strictFaceValidation);
  issues.push(...validationIssues);

  // FinalValidation ist bewusst nur noch ein zusätzlicher Check,
  // wenn die normale Validierung keine Issues gefunden hat.
  // Dadurch bekommen wir keine doppelten boundary_not_continuous-Meldungen.
  if (validationIssues.length === 0) {
    const finalIssues = finalValidation(building, debug);
    issues.push(...finalIssues);
  }

  const isValid = issues.length === 0;

  log(debug, 'Finished normalization', {
    wallCount: building.walls?.length ?? 0,
    roomCount: building.rooms?.length ?? 0,
    issueCount: issues.length,
    isValid,
  });

  return {
    building,
    issues,
    isValid,
  };
}